<template>
  <header ref="headerElement" :class="['fixed z-40 w-full shadow-md print:hidden', operator ? 'top-16 sm:top-10' : 'top-0']">
    <div
      class="relative z-[2] flex h-[2.125rem] items-center gap-3 border-b bg-[--header-top-bg-color] py-1 pe-4 ps-2.5 text-xs"
    >
      <ShipToSelector />

      <ExtensionPointList category="mobileHeader" />
    </div>

    <div class="relative z-[1]">
      <!-- region Default slot -->
      <transition :name="isAnimated ? 'slide-fade-top' : ''" mode="out-in">
        <div v-if="customSlots.default" class="relative z-20 bg-[--header-bottom-bg-color]">
          <component :is="customSlots.default" />
        </div>

        <div
          v-else
          class="relative z-20 flex h-14 w-full items-center justify-between gap-x-2 border-b bg-[--header-bottom-bg-color] sm:gap-x-6"
        >
          <!-- region Left slot -->
          <component :is="customSlots.left" v-if="customSlots.left" />

          <div v-else class="flex h-full items-center">
            <button
              :aria-label="$t('common.labels.main_menu')"
              type="button"
              class="h-full pe-1.5 ps-2.5 sm:pe-2.5"
              @click="mobileMenuVisible = true"
            >
              <VcIcon class="fill-primary" name="menu" :size="26" />
            </button>

            <router-link :to="$context.settings.default_return_url ?? '/'">
              <VcImage :src="logoUrl" :alt="$context.storeName" class="h-8" lazy />
            </router-link>
          </div>
          <!-- endregion Left slot -->

          <!-- region Right slot -->
          <component :is="customSlots.right" v-if="customSlots.right" />

          <div v-else class="flex h-full flex-row items-center pe-4">
            <a
              v-if="support_phone_number"
              :aria-label="$t('common.labels.support_phone_number')"
              class="px-1 py-2 xs:px-2"
              :href="`tel:${support_phone_number}`"
            >
              <VcIcon class="fill-primary" name="phone" :size="28" />
            </a>

            <button
              :aria-label="$t('common.labels.toggle_search_bar')"
              type="button"
              class="px-1 py-2 xs:px-2"
              @click="toggleSearchBar"
            >
              <VcIcon class="fill-primary" name="search" :size="28" />
            </button>

            <!-- Account Menu -->
            <button
              v-if="isAuthenticated"
              :aria-label="$t('common.links.account')"
              type="button"
              class="px-1 py-2 xs:px-2"
              @click="accountSidebarVisible = true"
            >
              <VcIcon class="fill-primary" name="user-circle" :size="28" />
            </button>

            <!-- Sign In/Sign Up -->
            <router-link
              v-else
              :to="{ name: ROUTES.SIGN_IN.NAME }"
              :aria-label="$t('shared.layout.header.link_sign_in')"
              class="px-1 py-2 xs:px-2"
            >
              <VcIcon class="fill-primary" name="user-circle" :size="28" />
            </router-link>

            <router-link
              :to="{ name: ROUTES.CART.NAME }"
              :aria-label="$t('common.links.cart')"
              class="px-1 py-2 xs:px-2"
            >
              <span class="relative block">
                <VcIcon class="fill-primary" name="cart" :size="28" />

                <transition
                  mode="out-in"
                  enter-from-class="scale-0"
                  leave-to-class="scale-0"
                  enter-active-class="will-change-transform"
                  leave-active-class="will-change-transform"
                >
                  <VcBadge
                    v-if="cart?.itemsQuantity"
                    variant="outline"
                    size="sm"
                    class="absolute -right-2 -top-2 transition-transform"
                    rounded
                    nowrap
                    max-width="none"
                  >
                    {{ $n(cart.itemsQuantity, { style: "decimal", notation: "compact" }) }}
                  </VcBadge>
                </transition>
              </span>
            </router-link>
          </div>
          <!-- endregion Right slot -->
        </div>
      </transition>
      <!-- endregion Default slot -->

      <!-- region Mobile Search Bar -->
      <MobileSearchBar :visible="searchBarVisible" />
      <!-- endregion Mobile Search Bar -->
    </div>
  </header>

  <!-- Height placeholder for mobile header due to fixed position -->
  <div :style="placeholderStyle" class="h-14 print:hidden"></div>

  <!-- Mobile menu -->
  <transition
    enter-from-class="-translate-x-full"
    leave-to-class="-translate-x-full"
    enter-active-class="will-change-transform"
    leave-active-class="will-change-transform"
  >
    <MobileMenu
      v-if="mobileMenuVisible"
      class="transition-transform print:hidden"
      :class="{ 'is-visible': mobileMenuVisible }"
      @close="mobileMenuVisible = false"
    />
  </transition>

  <!-- Account Sidebar -->
  <VcPopupSidebar
    :is-visible="accountSidebarVisible"
    :title="$t('common.links.account')"
    class="mobile-account-sidebar"
    @hide="accountSidebarVisible = false"
  >
    <!-- Account Section -->
    <div class="mb-6 flex flex-row gap-4 text-xl">
      <div class="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-primary">
        <VcImage v-if="user.photoUrl" :src="user.photoUrl" :alt="user.contact?.fullName" class="size-12" lazy />

        <VcIcon v-else name="user" />
      </div>

      <div class="flex flex-col leading-tight">
        <div class="flex flex-wrap items-center gap-x-1 text-neutral-900">
          <template v-if="operator">
            <span class="line-clamp-3 font-bold [word-break:break-word]">
              {{ operator.contact?.fullName || operator.userName }}
            </span>

            <span class="text-accent-200">
              {{ $t("shared.layout.header.top_header.logged_in_as") }}
            </span>
          </template>

          <span class="line-clamp-3 font-bold [word-break:break-word]">
            {{ user.contact?.fullName || user.userName }}
          </span>
        </div>

        <div>
          <button v-if="!operator" type="button" class="font-bold text-[--mobile-menu-navigation-color]" @click="signMeOut">
            {{ $t("shared.layout.header.link_logout") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Account Links -->
    <div class="space-y-3">
      <template v-for="item in filteredAccountMenuItems" :key="item.id">
        <router-link
          v-if="item.route"
          :to="item.route"
          class="flex items-center gap-3 rounded-md px-4 py-3 text-lg font-bold hover:bg-neutral-100"
          @click="accountSidebarVisible = false"
        >
          <VcIcon v-if="item.icon" :name="item.icon" class="fill-primary" />

          <span>{{ item.title }}</span>
        </router-link>
      </template>

      <template v-if="isCorporateMember">
        <template v-for="item in filteredCorporateMenuItems" :key="item.id">
          <router-link
            v-if="item.route"
            :to="item.route"
            class="flex items-center gap-3 rounded-md px-4 py-3 text-lg font-bold hover:bg-neutral-100"
            @click="accountSidebarVisible = false"
          >
            <VcIcon v-if="item.icon" :name="item.icon" class="fill-primary" />

            <span>{{ item.title }}</span>
          </router-link>
        </template>
      </template>
    </div>
  </VcPopupSidebar>
</template>

<script setup lang="ts">
import { syncRefs, useElementSize, useScrollLock } from "@vueuse/core";
import { computed, ref } from "vue";
import { useNavigations, useWhiteLabeling } from "@/core/composables";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { MODULE_XAPI_KEYS } from "@/core/constants/modules";
import { ROUTES } from "@/router/routes/constants";
import { useSignMeOut, useUser } from "@/shared/account";
import { useShortCart } from "@/shared/cart";
import { useNestedMobileHeader } from "@/shared/layout";
import { useSearchBar } from "@/shared/layout/composables/useSearchBar";
import { ShipToSelector } from "@/shared/ship-to-location";
import MobileMenu from "./mobile-menu/mobile-menu.vue";
import MobileSearchBar from "./mobile-search-bar.vue";
import type { ExtendedMenuLinkType } from "@/core/types";
import type { StyleValue } from "vue";

const mobileMenuVisible = ref(false);
const accountSidebarVisible = ref(false);
const headerElement = ref(null);

const { getSettingValue } = useModuleSettings(MODULE_XAPI_KEYS.MODULE_ID);
const support_phone_number = getSettingValue(MODULE_XAPI_KEYS.SUPPORT_PHONE_NUMBER);

const { customSlots, isAnimated } = useNestedMobileHeader();
const { searchBarVisible, toggleSearchBar } = useSearchBar();

const { height } = useElementSize(headerElement);
const { cart } = useShortCart();
const { logoUrl } = useWhiteLabeling();
const { isAuthenticated, user, operator, isCorporateMember } = useUser();
const { signMeOut } = useSignMeOut();
const { mobileAccountMenuItem, mobileCorporateMenuItem } = useNavigations();

function canShowItem(item: ExtendedMenuLinkType) {
  return !(item.id === "addresses" && isCorporateMember.value);
}

const filteredAccountMenuItems = computed<ExtendedMenuLinkType[]>(() => {
  const mobileItems = mobileAccountMenuItem.value?.children || [];
  return mobileItems.filter(canShowItem);
});

const filteredCorporateMenuItems = computed<ExtendedMenuLinkType[]>(() => {
  const mobileItems = mobileCorporateMenuItem.value?.children || [];
  return mobileItems.filter(canShowItem);
});

const placeholderStyle = computed<StyleValue | undefined>(() =>
  height.value ? { height: height.value + "px" } : undefined,
);

const isScrollLocked = computed(() => mobileMenuVisible.value || searchBarVisible.value || accountSidebarVisible.value);
const scrollLock = useScrollLock(document.body);

syncRefs(isScrollLocked, scrollLock);
</script>
